import React, { createContext, useCallback, useContext, useEffect } from "react";
import { useTabsStore } from "./tabsStore";

type TabPaneContextValue = {
  tabKey: string;
  active: boolean;
};

export const TabPaneContext = createContext<TabPaneContextValue>({
  tabKey: "",
  active: true,
});

/**
 * Lets a page set its own tab title (e.g. the request number once loaded).
 * No-op outside the tabbed workspace.
 */
export function useTabTitle(title?: string | null): void {
  const { tabKey } = useContext(TabPaneContext);
  const updateTitle = useTabsStore((s) => s.updateTitle);
  useEffect(() => {
    if (tabKey && title) updateTitle(tabKey, title);
  }, [tabKey, title, updateTitle]);
}

/**
 * True when this tab is the visible one. Pages can use it to pause polling or
 * expensive work while backgrounded.
 */
export function useIsTabActive(): boolean {
  return useContext(TabPaneContext).active;
}

/**
 * Closes the tab this page is rendered in.
 *
 * Call it *before* navigating away: TabbedWorkspace opens a tab for whatever
 * pathname is current, so closing after the navigation would race with that
 * effect and leave the old tab behind.
 */
export function useCloseOwnTab(): () => void {
  const { tabKey } = useContext(TabPaneContext);
  const removeTab = useTabsStore((s) => s.removeTab);
  return useCallback(() => {
    if (tabKey) removeTab(tabKey);
  }, [tabKey, removeTab]);
}

export const TabPaneProvider: React.FC<
  React.PropsWithChildren<TabPaneContextValue>
> = ({ tabKey, active, children }) => (
  <TabPaneContext.Provider value={{ tabKey, active }}>
    {children}
  </TabPaneContext.Provider>
);
